import Box from '@mui/material/Box';

import { Reveal } from '@/components/atoms';
import { DeliveryTikTokTile, DeliveryTile } from '@/components/deliveries';
import { type DeliveryGalleryItem, type DeliveryOrientation } from '@/services/deliveries';

type Props = {
  items: readonly DeliveryGalleryItem[];
  onOpen: (item: DeliveryGalleryItem) => void;
};

/**
 * How many row units a tile of each shape takes.
 *
 * The unit below is about a third of a column, so these spans are the tile's
 * own proportions: a landscape photograph is two thirds as tall as it is wide,
 * a portrait one is a third taller than it is wide. That is the whole masonry
 * mechanism — no measuring, no JavaScript, no library.
 */
const ROW_SPAN: Record<DeliveryOrientation, number> = {
  landscape: 2,
  square: 3,
  portrait: 4,
};

/**
 * One row unit at each breakpoint: roughly a third of a column's width there.
 *
 * It has to be restated per breakpoint because CSS cannot divide a column's
 * own width — a `fr` is not readable in `grid-auto-rows`. The numbers are read
 * off the real column widths inside this page's `lg` container: two columns on
 * a phone (~171px), three from `sm` (~229), three wider ones from `md` (~340),
 * four from `lg` (~276).
 */
const ROW_UNIT = { xs: 57, sm: 76, md: 113, lg: 92 };

/**
 * The wall of handovers.
 *
 * ## Masonry in CSS grid, not in columns or in a library
 *
 * `column-count` is the usual one-line answer to masonry and it is wrong here:
 * it fills column one top to bottom before starting column two, so a
 * newest-first archive reads down the left edge and then jumps back up — and
 * Tab follows it. A grid with row spans keeps the document order the archive
 * is sorted in, so the oldest car is last for a reader, a crawler and a
 * keyboard alike.
 *
 * ## `dense`, and what it costs
 *
 * Without it, a portrait tile beside two landscape ones leaves a visible hole,
 * and a wall of photographs with gaps punched in it looks broken rather than
 * designed. `dense` backfills those holes with the next tile that fits, which
 * means a tile can be pulled up past one or two older ones. Accepted
 * deliberately: the trade is a local reordering inside one band of the wall
 * against holes down the whole of it, and the newest-to-oldest sweep survives
 * either way.
 *
 * ## The reveal is per tile and capped
 *
 * `Reveal` only animates what was below the fold when the page became
 * interactive, so the first screenful never flashes. The stagger is modulo
 * four, so loading the tenth page does not produce a tile that fades in two
 * seconds after the one above it.
 */
export function DeliveryGalleryGrid({ items, onOpen }: Props) {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'grid',
        gap: 4,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gridAutoRows: ROW_UNIT,
        gridAutoFlow: 'row dense',
      }}
    >
      {items.map((item, index) => (
        <Box
          component="li"
          key={item.media.id}
          sx={{ gridRow: `span ${ROW_SPAN[item.media.orientation]}`, minWidth: 0 }}
        >
          <Reveal delay={(index % 4) * 70} sx={{ height: '100%' }}>
            {item.media.kind === 'tiktok' ? (
              <DeliveryTikTokTile item={item} />
            ) : (
              <DeliveryTile
                item={item}
                onOpen={onOpen}
                sizes="(min-width: 1200px) 276px, (min-width: 900px) 340px, (min-width: 600px) 229px, 50vw"
              />
            )}
          </Reveal>
        </Box>
      ))}
    </Box>
  );
}
